// ── backupImport.js — 셈핏 백업 엑셀 → Firestore 복원 ────────────────
// exportFullBackup이 만든 엑셀을 읽어서 데이터를 되돌리는 유틸.
// ⚠️ 파괴적 작업: 호출 전에 반드시 snapshots.createSnapshot 호출하는 게 안전.

import { collection, getDocs, addDoc, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../db';

// ── 엑셀 파일 → 파싱된 데이터 객체 ──
// 결과 형태:
// {
//   meta: { backupDate, uid, recipeCount, ... },
//   transactions: [...],
//   purchaseLog: [...],
//   prices: [...],
//   categories: [{ name, items: [...] }],
//   employees: [...],
//   schedules: { '2025-01': { 15: { empId: { blocks: [...] } } } },
//   profitLoss: { '2025-01': { revenue, costs, memo } },
//   inventory: { items: { '재료명': { currentStock, ... } } },
//   orderHistory: [...],
//   payrollSettings: { storeName, ... },
// }
export async function parseBackupExcel(file) {
  if (typeof window === 'undefined' || !window.XLSX) throw new Error('엑셀 라이브러리 로드 실패');
  const XLSX = window.XLSX;

  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });

  const sheet = (name) => {
    const ws = wb.Sheets[name];
    if (!ws) return [];
    return XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  };

  // 헤더 + 행 → 객체 배열로
  const rows = (aoa) => {
    if (!aoa.length) return [];
    const headers = aoa[0];
    return aoa.slice(1)
      .filter(r => r.some(c => c !== '' && c !== null && c !== undefined))
      .map(r => {
        const o = {};
        headers.forEach((h, i) => { o[h] = r[i]; });
        return o;
      });
  };

  const num = (v) => Number(v) || 0;
  const str = (v) => (v === null || v === undefined) ? '' : String(v);

  // ── 백업정보 ──
  const metaRows = rows(sheet('백업정보'));
  const meta = {};
  for (const r of metaRows) meta[r['항목']] = r['값'];

  // ── 거래장부 ──
  const txRaw = rows(sheet('거래장부'));
  const transactions = txRaw.map(r => ({
    id: str(r['ID']) || undefined,
    date: str(r['날짜']),
    type: str(r['유형']),
    partnerName: str(r['거래처']),
    itemName: str(r['품목']),
    supply: num(r['공급가액']),
    vat: num(r['부가세']),
    taxFreeAmount: num(r['면세금액']),
    total: num(r['총액']),
    category: {
      major: str(r['category.major']) || null,
      sub: str(r['category.sub']) || null,
    },
    revenueCategory: str(r['결제수단']) || null,
    evidence: r['증빙'] ? { type: str(r['증빙']) } : null,
    memo: str(r['메모']),
    source: str(r['source']) || null,
    receiptFingerprint: str(r['receiptFingerprint']) || null,
  }));

  // ── 단가표 ──
  const pricesRaw = rows(sheet('단가표'));
  const prices = pricesRaw.map(r => ({
    code: str(r['코드']),
    name: str(r['재료명']),
    spec: str(r['규격']),
    unit: str(r['단위']),
    price: num(r['단가']),
    net_g: num(r['용량(net_g)']),
    category: str(r['카테고리']),
    vendor: str(r['거래처']),
    aiCanonical: str(r['AI매칭']),
    // V112: 없는 열(옛 백업)은 빈 값 → 아래에서 빈 칸은 지운다
    maker: str(r['제조사']),
    name_zh: str(r['중국어명']),
    maker_zh: str(r['제조사(中)']),
    vendor_zh: str(r['거래처(中)']),
    spec_zh: str(r['규격(中)']),
    unit_zh: str(r['단위(中)']),
    link: str(r['구매 링크']),   // V118
    loss_g: num(r['로스(g)']),   // V152 신선식품 로스
  })).map(p => {
    const o = { ...p };
    for (const k of ['maker', 'name_zh', 'maker_zh', 'vendor_zh', 'spec_zh', 'unit_zh', 'link', 'loss_g']) if (!o[k]) delete o[k];
    return o;
  });

  // ── 레시피 (요약 + 재료) 합치기 ──
  // 우선순위: 레시피_재료 시트가 있으면 그걸 ground truth로 (재료 디테일 풍부).
  //         없으면 레시피 시트의 "재료(이름:중량)" 컬럼을 파싱.
  const recipeRows = rows(sheet('레시피'));
  const ingRows = rows(sheet('레시피_재료'));

  // categories 재구성: 카테고리명 → { name, items: [...] }
  const catMap = new Map();
  // 1단계: 레시피 요약 시트로 레시피 뼈대 생성
  for (const r of recipeRows) {
    const catName = str(r['카테고리']);
    if (!catName) continue;
    if (!catMap.has(catName)) catMap.set(catName, { name: catName, items: [] });
    const id = str(r['ID']) || `r_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const item = {
      id,
      name: str(r['이름']),
      type: str(r['유형']) || 'menu',
      total_output: num(r['총생산량']),
      serving_g: num(r['인분(g)']),
      selling_price: num(r['판매가']),
      ingredients: [],  // 아래에서 채움
      note: str(r['메모']),
    };
    // fallback: "재료(이름:중량)" 컬럼 파싱
    const ingStr = str(r['재료(이름:중량)']);
    if (ingStr) {
      item.ingredients = ingStr.split(';').map(s => s.trim()).filter(Boolean).map(s => {
        const [name, qty] = s.split(':');
        return { name: (name || '').trim(), qty: num(qty) };
      });
    }
    catMap.get(catName).items.push(item);
  }
  // 2단계: 레시피_재료 시트가 있으면 ingredients 덮어쓰기 (id 기준 매칭)
  if (ingRows.length > 0) {
    // 먼저 (catName, recipeId) → item 인덱스 만들기
    const itemIdx = new Map();
    for (const [catName, cat] of catMap.entries()) {
      for (const item of cat.items) {
        itemIdx.set(`${catName}|${item.id}`, item);
        // id 없을 때 대비 — 이름으로도 매칭 가능하게
        itemIdx.set(`${catName}|name:${item.name}`, item);
      }
    }
    // ingredients 초기화 (덮어쓸 거니까)
    const touched = new Set();
    for (const r of ingRows) {
      const catName = str(r['카테고리']);
      const recipeId = str(r['레시피ID']);
      const recipeName = str(r['레시피명']);
      const key1 = `${catName}|${recipeId}`;
      const key2 = `${catName}|name:${recipeName}`;
      const item = itemIdx.get(key1) || itemIdx.get(key2);
      if (!item) continue;
      if (!touched.has(item)) { item.ingredients = []; touched.add(item); }
      item.ingredients.push({
        name: str(r['재료명']),
        qty: num(r['사용량']),
      });
    }
  }
  const categories = Array.from(catMap.values());

  // ── 직원 ──
  const empRaw = rows(sheet('직원'));
  const employees = empRaw.map(r => ({
    id: str(r['ID']) || `e_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name: str(r['이름']),
    role: str(r['직위']),
    employmentType: str(r['고용형태']),
    wage: num(r['시급/월급']),
    hourlyWage: num(r['기준시급']),
    hireDate: str(r['입사일']),
    phone: str(r['연락처']),
    memo: str(r['메모']),
  }));

  // ── 스케줄 ──
  const schedRaw = rows(sheet('스케줄'));
  const schedules = {};
  for (const r of schedRaw) {
    const mk = str(r['월']);
    const day = str(r['일']);
    const empId = str(r['직원ID']);
    const bt = str(r['근무시간']);
    const note = str(r['메모']);
    if (!mk || !day || !empId) continue;
    if (!schedules[mk]) schedules[mk] = {};
    if (!schedules[mk][day]) schedules[mk][day] = {};
    // bt 형식: "09:00-12:00; 13:00-18:00" 또는 "09:00-18:00"
    const blocks = bt.split(';').map(s => s.trim()).filter(Boolean).map(s => {
      const [start, end] = s.split('-').map(x => x.trim());
      return { start, end };
    });
    schedules[mk][day][empId] = blocks.length > 1
      ? { blocks, note }
      : { start: blocks[0]?.start || '', end: blocks[0]?.end || '', note };
  }

  // ── 손익수기 ──
  const plRaw = rows(sheet('손익수기'));
  const profitLoss = {};
  for (const r of plRaw) {
    const mk = str(r['월']);
    if (!mk) continue;
    profitLoss[mk] = {
      revenue: {
        card: num(r['카드(수기)']),
        cash: num(r['현금(수기)']),
        delivery: num(r['배달(수기)']),
      },
      costs: {
        ingredients: num(r['식재료비']),
        labor: num(r['인건비']),
        rent: num(r['임대료']),
        utilities: num(r['공과금']),
        other: num(r['기타']),
      },
      memo: str(r['메모']),
    };
  }

  // ── 매입메모 ──
  const logRaw = rows(sheet('매입메모'));
  const purchaseLog = logRaw.map(r => ({
    id: str(r['ID']) || undefined,
    date: str(r['날짜']),
    partnerName: str(r['거래처']),
    evidenceType: str(r['증빙유형']),
    supply: num(r['공급가액']),
    vat: num(r['부가세']),
    taxFreeAmount: num(r['면세금액']),
    total: num(r['총액']),
    items: [],  // 백업에는 품목 수만 있고 디테일 없음 → 빈 배열
    memo: str(r['메모']),
  }));

  // ── 발주내역 ──
  const orderRaw = rows(sheet('발주내역'));
  const orderHistory = orderRaw.map(r => ({
    id: str(r['ID']) || `o_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    date: str(r['날짜']),
    title: str(r['제목']),
    items: [],  // 디테일 없음
    totalCost: num(r['총금액']),
    memo: str(r['메모']),
  }));

  // ── 재고 ──
  const invRaw = rows(sheet('재고'));
  const inventory = { items: {} };
  for (const r of invRaw) {
    const name = str(r['재료명']);
    if (!name) continue;
    inventory.items[name] = {
      currentStock: num(r['현재고']),
      safetyStock: num(r['안전재고']),
      unit: str(r['단위']),
      memo: str(r['메모']),
    };
  }

  // ── 설정 ──
  const settingsRaw = rows(sheet('설정'));
  const settingsMap = {};
  for (const r of settingsRaw) settingsMap[r['항목']] = r['값'];
  const payrollSettings = {
    storeName: str(settingsMap['매장명']),
    address: str(settingsMap['주소']),
    businessNumber: str(settingsMap['사업자번호']),
    industry: str(settingsMap['업종']),
    expectedMonthlyServings: num(settingsMap['예상 월 인분']),
    useWeeklyHolidayPay: settingsMap['주휴수당 사용'] === 'O',
    autoInsurance: settingsMap['4대보험 자동'] === 'O',
  };

  return {
    meta,
    transactions, purchaseLog,
    prices, categories,
    employees, schedules,
    profitLoss, inventory,
    orderHistory, payrollSettings,
  };
}

// ── 미리보기용 — 파싱된 데이터의 건수 ──
export function summarizeParsed(parsed) {
  return {
    backupDate: parsed.meta?.['백업 일시'] || '(알 수 없음)',
    transactions: parsed.transactions.length,
    purchaseLog: parsed.purchaseLog.length,
    prices: parsed.prices.length,
    recipes: parsed.categories.reduce((s, c) => s + c.items.length, 0),
    employees: parsed.employees.length,
    schedules: Object.keys(parsed.schedules).length,
    profitLossMonths: Object.keys(parsed.profitLoss).length,
    orderHistory: parsed.orderHistory.length,
    inventoryItems: Object.keys(parsed.inventory.items || {}).length,
  };
}

// ── 복원 실행 ──
// writers: { updateCategories, updatePrices, updateProfitLoss, updateEmployees,
//            updateSchedules, updateInventory, updatePayrollSettings }
// options: { mode: 'replace' | 'append', sections: { transactions, purchaseLog, ... bool } }
export async function restoreFromBackup(uid, parsed, writers, options) {
  if (!uid) throw new Error('로그인 필요');
  const opts = options || {};
  const mode = opts.mode || 'replace';
  const sections = opts.sections || {};
  const onProgress = opts.onProgress || (() => {});
  const result = { restored: {}, errors: [] };

  // ── 1) 서브컬렉션: transactions ──
  if (sections.transactions !== false) {
    try {
      onProgress('거래장부 복원 중...');
      if (mode === 'replace') {
        const snap = await getDocs(collection(db, 'users', uid, 'transactions'));
        for (const d of snap.docs) {
          try { await deleteDoc(d.ref); } catch (e) { result.errors.push('delete failed: ' + e.message); }
        }
      }
      let added = 0;
      for (const tx of (parsed.transactions || [])) {
        const { id, ...data } = tx;
        try {
          await addDoc(collection(db, 'users', uid, 'transactions'), data);
          added++;
        } catch (e) { result.errors.push('tx ' + (id || '?') + ': ' + e.message); }
      }
      result.restored.transactions = added;
    } catch (e) { result.errors.push('transactions: ' + e.message); }
  }

  // ── 2) 서브컬렉션: purchaseLog ──
  if (sections.purchaseLog !== false) {
    try {
      onProgress('매입메모 복원 중...');
      if (mode === 'replace') {
        const snap = await getDocs(collection(db, 'users', uid, 'purchaseLog'));
        for (const d of snap.docs) {
          try { await deleteDoc(d.ref); } catch (e) { result.errors.push('delete failed: ' + e.message); }
        }
      }
      let added = 0;
      for (const log of (parsed.purchaseLog || [])) {
        const { id, ...data } = log;
        try {
          await addDoc(collection(db, 'users', uid, 'purchaseLog'), data);
          added++;
        } catch (e) { result.errors.push('log ' + (id || '?') + ': ' + e.message); }
      }
      result.restored.purchaseLog = added;
    } catch (e) { result.errors.push('purchaseLog: ' + e.message); }
  }

  // ── 3) 단일 문서 필드들 — writers 호출 (암호화·모드 처리는 writer가 알아서) ──
  const writeField = async (label, sectionKey, writerName, value) => {
    if (sections[sectionKey] === false) return;
    const fn = writers && writers[writerName];
    if (!fn) { result.errors.push(`${label}: writer ${writerName} 없음`); return; }
    try {
      onProgress(label + ' 복원 중...');
      await fn(value);
      result.restored[sectionKey] = Array.isArray(value)
        ? value.length
        : (typeof value === 'object' ? Object.keys(value).length : 1);
    } catch (e) { result.errors.push(label + ': ' + e.message); }
  };

  await writeField('단가표', 'prices', 'updatePrices', parsed.prices || []);
  await writeField('레시피', 'categories', 'updateCategories', parsed.categories || []);
  await writeField('직원', 'employees', 'updateEmployees', parsed.employees || []);
  await writeField('스케줄', 'schedules', 'updateSchedules', parsed.schedules || {});
  await writeField('손익수기', 'profitLoss', 'updateProfitLoss', parsed.profitLoss || {});
  await writeField('재고', 'inventory', 'updateInventory', parsed.inventory || { items: {} });
  await writeField('설정', 'payrollSettings', 'updatePayrollSettings', parsed.payrollSettings || {});

  // 발주내역은 saveOrderHistory(entry)가 1건씩 추가하는 형태 → 직접 setDoc
  if (sections.orderHistory !== false && (parsed.orderHistory || []).length > 0) {
    try {
      onProgress('발주내역 복원 중...');
      await setDoc(doc(db, 'users', uid), { orderHistory: parsed.orderHistory }, { merge: true });
      result.restored.orderHistory = parsed.orderHistory.length;
    } catch (e) { result.errors.push('orderHistory: ' + e.message); }
  }

  return result;
}
